import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Award, MapPin, Clock, Guitar, Target, Package, Wrench, CheckCircle, Star } from 'lucide-react';

const AboutPage: React.FC = () => {
  useEffect(() => {
    document.title = "About Us | Strum Music";
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-red-700 py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-r from-red-800 to-red-600"></div>
          <img 
            src="https://images.pexels.com/photos/165971/pexels-photo-165971.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
            alt="Music instruments" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <Guitar className="h-16 w-16 mx-auto mb-4 text-white" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">About Strum Music</h1>
            <p className="text-xl text-white max-w-3xl mx-auto">
              Your one-stop destination for everything musical — from world-class instruments to pro-level audio gear.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main About Content */}
      <section className="py-16">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="prose prose-lg max-w-none"
            >
              <div className="text-center mb-12">
                <p className="text-lg text-gray-700 leading-relaxed">
                  Strum Music is your one-stop destination for everything musical — from world-class instruments to pro-level audio gear. 
                  Based in India, we specialize in selling, renting, and servicing a wide range of instruments, including guitars, keyboards, 
                  percussions, Indian classical instruments, studio equipment, and accessories.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mt-4">
                  Whether you're a beginner just starting your journey or a professional performing on stage, we have something for everyone.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <Target className="h-12 w-12 text-red-700 mr-4" />
              <h2 className="text-3xl font-bold text-black">Our Mission</h2>
            </div>
            <p className="text-xl text-gray-700 leading-relaxed">
              To empower musicians, artists, and creators with high-quality, affordable gear and unmatched support — 
              making music accessible and enjoyable for all.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16">
        <div className="container-custom">
          <div className="text-center mb-12">
            <motion.h2 
              className="text-3xl font-bold text-black mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              What We Offer
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Guitar className="h-8 w-8 text-red-700" />,
                title: "Instruments for Sale",
                description: "From Yamaha, Fender, Roland, and more premium brands."
              },
              {
                icon: <Clock className="h-8 w-8 text-red-700" />,
                title: "Rental Services",
                description: "Short-term rentals for events, sessions, and students."
              },
              {
                icon: <Package className="h-8 w-8 text-red-700" />,
                title: "Studio Equipment",
                description: "Mics, mixers, headphones, and recording gear."
              },
              {
                icon: <Star className="h-8 w-8 text-red-700" />,
                title: "Open Box & Factory Returned Deals",
                description: "Value products with full transparency and warranties."
              },
              {
                icon: <Wrench className="h-8 w-8 text-red-700" />,
                title: "Repair & Support",
                description: "Maintenance and part replacement services."
              },
              {
                icon: <Award className="h-8 w-8 text-red-700" />,
                title: "Expert Guidance",
                description: "Professional advice to help you choose the right gear."
              }
            ].map((service, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-lg shadow-sm p-6 text-center border border-gray-100 hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-semibold text-black mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-black mb-4">Why Choose Us?</h2>
            <p className="text-gray-600">
              We're committed to providing the best musical experience with quality products and exceptional service.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Trusted brands & genuine products",
              "Competitive pricing & exclusive discounts",
              "Dedicated customer service & support",
              "Fast shipping & easy returns",
              "Multiple payment & EMI options",
              "Expert advice from musicians"
            ].map((benefit, index) => (
              <motion.div 
                key={index}
                className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Visit Us Section */}
      <section className="py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <MapPin className="h-12 w-12 text-red-700 mr-4" />
              <h2 className="text-3xl font-bold text-black">Visit Us</h2>
            </div>
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              We serve musicians across India and aim to become a trusted name in every music room, classroom, stage, and studio.
            </p>
            
            <div className="bg-red-50 rounded-lg p-8 border border-red-100">
              <h3 className="text-xl font-semibold text-black mb-4">Our Store Location</h3>
              <div className="text-gray-700">
                <p className="mb-2"><strong>Address:</strong> C-7/13, Lawrence Road, Keshav Puram, Delhi 110035</p>
                <p className="mb-2"><strong>Phone:</strong> +91-8076883098</p>
                <p className="mb-2"><strong>Email:</strong> strum.music.co@gmail.com</p>
                <p><strong>Store Hours:</strong> Tuesday - Sunday: 11:30am - 8:30pm (Closed Mondays)</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-black mb-4">Our Values</h2>
            <p className="text-gray-600">
              These core principles guide everything we do at Strum Music, from the products we select to the service we provide.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Award className="h-8 w-8 text-red-700" />,
                title: "Quality",
                description: "We curate only the finest instruments and accessories, thoroughly testing each product to ensure exceptional performance and reliability."
              },
              {
                icon: <Users className="h-8 w-8 text-red-700" />,
                title: "Expertise",
                description: "Our team consists of passionate musicians with years of experience, ready to provide knowledgeable advice and personalized recommendations."
              },
              {
                icon: <Clock className="h-8 w-8 text-red-700" />,
                title: "Service",
                description: "We're committed to providing exceptional customer service before, during, and after your purchase with speedy delivery and responsive support."
              }
            ].map((value, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-lg shadow-sm p-6 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-black mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;